import {Box, Typography} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {GridRowOrderChangeParams} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import Papa from 'papaparse';
import React, {useEffect, useRef, useState} from 'react';
import {validateTerms} from '../../../../../api/STRService';
import {ReactComponent as DownloadIcon} from '../../../../../assests/svg/download-icon.svg';
import {colorArray, STR_TERMS_TABS} from '../../../../../constants/constants';
import {useToast} from '../../../../../contexts/toastContext/ToastContext';
import {ReactComponent as InfoIcon} from '../../../../../assests/svg/info_icon_outlined_blue.svg';
import {
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_TEXT_COLOR,
  Terms,
  updateRowPosition,
} from '../../../../../utils/createSTRUtils';
import {
  isValidJsonObject,
  QueryValidationType,
  unwrapQueryText,
  validateQueryV2,
  wrapQueryBeforeSymbol,
} from '../../../../../utils/utils';
import AurButton from '../../../../common/aur-button/AurButton';
import {AurColorPickerOptionsProp} from '../../../../common/aur-color-picker/AurColorPicker';
import AurPreviousNextNavigation from '../../../../common/aur-navigation/AurPreviousNextNavigation';
import AurPopup from '../../../../common/aur-popup/AurPopup';
import {ScopeContentType} from '../ScopeContent';
import AurCenteredTabs from './AurCenteredTabs';
import AurSlateEditor, {TermData} from './AurSlateEditor';
import TermsContentHeader from './TermsContentHeader';
import TermsContentMiddle from './TermsContentMiddle';
import TermsContentRight from './TermsContentRight';
import {showErrorToast} from '../../../../../api/utils';
import AurTooltip from '../../../../common/aur-tooltip/AurTooltip';
import QueryWarningPopupAlert from '../../../index-search/QueryWarningPopupAlert';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../../hooks/useAnalytics';

interface TermsContentProps {
  initialData: Terms[];
  onDataChange: (data: Terms[]) => void;
  initialAPIResponseData?: Terms[];
  clientId: string;
  projectId: string | null | undefined;
  scopeContentData: ScopeContentType | null;
  setIsLoading: (isLoading: boolean) => void;
}

interface TermsElasticQueryResult {
  term: string;
  elasticDsl: string;
  errors: string[];
  warnings: string[];
  success: boolean;
}

const TermsContent = (props: TermsContentProps) => {
  const {showToast} = useToast();
  const {
    initialData,
    onDataChange,
    initialAPIResponseData,
    projectId,
    clientId,
    scopeContentData,
    setIsLoading,
  } = props;
  const [selectedTab, setSelectedTab] = useState(1);
  const {trackGAEvent} = useAnalytics();

  const [terms, setTerms] = useState<TermData[] | []>([]);
  const [termsWithColorCodes, setTermsWithColorCodes] =
    useState<Terms[]>(initialData);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [isDuplicateTermsHighlighted, setIsDuplicateTermsHighlighted] =
    useState<boolean>(false);
  const [currentSelectedErrorTerm, setCurrentSelectedErrorTerm] =
    useState<number>(0);
  /**
   * termsWithHighlightColorMap used to tracked existing selected color for term when it move to
   * left from right and if user again move it from left to right then look for term in this array
   * if it already exist then used exiting color instead of default one for term
   */
  const [termsWithHighlightColorMap, setTermsWithHighlightColorMap] = useState<
    Map<string, string>
  >(new Map());
  const [showQueryWarningModal, setShowQueryWarningModal] = useState<{
    visible: boolean;
    errorTypes?: QueryValidationType[];
  }>({visible: false, errorTypes: []});

  const handleTabSelection = (selectedTab: number) => {
    setSelectedTab(selectedTab);
    const gaLabel = tabsArray[selectedTab];
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: gaLabel,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.str.addTermsViewAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.steps.addTerms'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isManualErrorSelection = useRef(false);
  const [termsValidationData, setTermsValidationData] = useState<
    TermsElasticQueryResult[] | []
  >();
  const [announcement, setAnnouncement] = useState('');

  const tabsArray = [
    t('eca.searchTermReport.termsContent.fieldEditor'),
    t('eca.searchTermReport.termsContent.splitView'),
    t('eca.searchTermReport.termsContent.termsList'),
  ];

  const onChangeTerms = (termsList: TermData[]) => {
    if (JSON.stringify(termsList) !== JSON.stringify(terms)) {
      setTerms(termsList);
    }
  };

  useEffect(() => {
    setTermsWithColorCodes(initialData);
  }, [initialData]);

  const handleCsvUpload: React.MouseEventHandler<HTMLButtonElement> = () => {
    fileInputRef.current?.click();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.importCsv'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.str.importCsvAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.steps.addTerms'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        complete: (result) => {
          const parsedData = (
            result.data.flat().filter(Boolean) as string[]
          ).map((term) => ({
            value: term,
            isAPIErrorTerm: false,
          }));
          const previousData = terms;
          const mappedTerms = [...previousData, ...parsedData];
          setTerms(mappedTerms);
          setIsDuplicateTermsHighlighted(false);
        },
        skipEmptyLines: true,
      });
      event.target.value = '';
    }
  };

  const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
    const newRows = await updateRowPosition(
      params.oldIndex,
      params.targetIndex,
      termsWithColorCodes
    );
    const updatedRows = newRows.map((row: Terms, index: number) => ({
      ...row,
      id: index + 1,
    }));
    setTermsWithColorCodes(updatedRows);
  };

  const [termsConfirmationModal, setTermsConfirmationModal] = useState(false);

  const handleMoveToRightClick = () => {
    const collectedErrors = new Set<QueryValidationType>();
    for (const term of terms) {
      const result = validateQueryV2(term.value);

      result.errors.forEach((error) => {
        collectedErrors.add(error);
      });

      // 🚨 Stop if all three possible error found
      if (
        collectedErrors.has('UNSUPPORTED') &&
        collectedErrors.has('LOWERCASE_OPERATORS') &&
        collectedErrors.has('PROXIMITY_P') &&
        collectedErrors.has('PROXIMITY_W')
      ) {
        break;
      }
    }

    if (collectedErrors.size > 0) {
      // Show query warning modal as some terms contains invalid dtSyntax
      setShowQueryWarningModal({
        visible: true,
        errorTypes: Array.from(collectedErrors),
      });
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.str.syntaxError'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    } else {
      moveTermsToRight();
    }
  };

  const moveTermsToRight = async (
    isAPICalled = false,
    shouldAddErrorTerms = false,
    localValidationErrorShowed = false,
    termValidationDataLatest?: TermsElasticQueryResult[] | []
  ) => {    
    let uniqueTerms: string[] = [];
    const duplicateTerms: string[] = [];
    terms?.forEach((item) => {
      if (
        uniqueTerms.some(
          (term) =>
            term.trim().toLowerCase() === item.value.trim().toLowerCase()
        )
      ) {
        duplicateTerms.push(item.value);
      } else {
        uniqueTerms.push(item.value.trim());
      }
    });
    uniqueTerms.forEach((item) => {
      if (
        termsWithColorCodes.some(
          (term) => term.term.trim().toLowerCase() === item.trim().toLowerCase()
        )
      ) {
        uniqueTerms = uniqueTerms.filter(
          (term) => term.trim().toLowerCase() !== item.trim().toLowerCase()
        );
        duplicateTerms.push(item);
      }
    });
    if (isAPICalled && (termValidationDataLatest ?? termsValidationData)) {
      const successTerms: string[] = [];
      const nonSuccessTerms: string[] = [];
      uniqueTerms.forEach((term) => {
        const normalizedTerm = term.toLowerCase();
        const matchingTerm = (
          termValidationDataLatest ??
          termsValidationData ??
          []
        ).find((data) => {
          return unwrapQueryText(data.term).toLowerCase() === normalizedTerm;
        });
        if (matchingTerm?.success) {
          successTerms.push(term);
        } else {
          nonSuccessTerms.push(term);
        }
      });
      if (shouldAddErrorTerms) {
        continueTermsProcessing(
          [...successTerms, ...nonSuccessTerms],
          duplicateTerms.map((term: string) => ({
            value: term,
            isAPIErrorTerm: false,
            isDuplicateTerm: true,
          }))
        );
      } else {
        const mappedDuplicateTerms = duplicateTerms.map((term: string) => ({
          value: term,
          isAPIErrorTerm: false,
          isDuplicateTerm: true,
        }));
        mappedDuplicateTerms.push(
          ...nonSuccessTerms.map((term: string) => ({
            value: term,
            isAPIErrorTerm: true,
            isDuplicateTerm: false,
          }))
        );
        continueTermsProcessing(successTerms, mappedDuplicateTerms);
      }
    } else {
      await handleAPICallAndTermValidation(
        uniqueTerms,
        duplicateTerms,
        localValidationErrorShowed
      );
    }
  };

  const handleAPICallAndTermValidation = async (
    uniqueTerms: string[],
    duplicateTerms: string[],
    localValidationErrorShowed: boolean // For track local validation show error alert
  ) => {
    try {
      if (uniqueTerms.length > 0) {
        setIsLoading(true);
        const requestBody = {
          terms: uniqueTerms.map((term) => wrapQueryBeforeSymbol(term.trim())),
        };
        const isValidJson = isValidJsonObject(JSON.stringify(requestBody));
        if (!isValidJson) {
          throw new Error('Not a valid JSON Payload');
        }
        const responseFromAPI = await validateTerms(
          scopeContentData?.fieldTemplateId ?? 0,
          clientId,
          projectId ?? '',
          requestBody
        );
        setTermsValidationData(responseFromAPI.data?.data);

        const nonSuccessTerms: string[] = [];
        uniqueTerms.forEach((term) => {
          const normalizedTerm = term.toLowerCase();
          const matchingTerm = responseFromAPI.data?.data?.find(
            (data: TermsElasticQueryResult) => {
              return (
                unwrapQueryText(data.term).toLowerCase() === normalizedTerm
              );
            }
          );
          if (!matchingTerm?.success) {
            nonSuccessTerms.push(term);
          }
        });
        /**
         * term error alert already visible from local regex so not showing again after
         * found error from api response to avoid multiple alert back to back
         * Jira Comment: https://consilio.atlassian.net/browse/ARHD-17593?focusedCommentId=501093
         */
        if (localValidationErrorShowed && nonSuccessTerms.length > 0) {
          moveTermsToRight(true, true, true, responseFromAPI.data?.data);
        } else if (nonSuccessTerms.length > 0) {
          setTermsConfirmationModal(true);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
            subPage: {
              subpage_name: t('gaTracking.str.queryError'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        } else {
          duplicateTerms.push(...nonSuccessTerms);
          continueTermsProcessing(
            uniqueTerms,
            duplicateTerms.map((term: string) => ({
              value: term,
              isAPIErrorTerm: false,
              isDuplicateTerm: true,
            }))
          );
        }
      } else {
        continueTermsProcessing(
          uniqueTerms,
          duplicateTerms.map((term: string) => ({
            value: term,
            isAPIErrorTerm: false,
            isDuplicateTerm: true,
          }))
        );
      }
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'center');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsModalOkClick = () => {
    moveTermsToRight(true, true);
    setTermsConfirmationModal(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.queryIssueOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.queryError'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleTermsModalCancelClick = () => {
    moveTermsToRight(true, false);
    setTermsConfirmationModal(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.queryIssueCancelAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.queryError'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const continueTermsProcessing = (
    uniqueTerms: string[],
    duplicateTerms: TermData[]
  ) => {
    const updatedTerms = [...uniqueTerms]
      .filter((term) => term.length > 0)
      ?.map<Terms>((term: string, index: number) => {
        let textColor = DEFAULT_TEXT_COLOR;
        let bgColor = DEFAULT_BACKGROUND_COLOR;
        if (termsWithHighlightColorMap.has(term)) {
          bgColor = termsWithHighlightColorMap.get(term) as string;
          textColor =
            colorArray.find((item) => item.backgroundColor === bgColor)
              ?.textColor ?? DEFAULT_TEXT_COLOR;
        }
        return {
          id: termsWithColorCodes.length + index + 1,
          textColor: textColor,
          backgroundColor: bgColor,
          term,
        };
      });
    const updatedTermsList = updatedTerms
      .map((termObj) => {
        const match = initialAPIResponseData?.find(
          (activeTerm) =>
            activeTerm.term.trim().toLowerCase() ===
            termObj.term.trim().toLowerCase()
        );
        return match ? {...termObj, termId: match.termId} : termObj;
      })
      .filter((termObj) => termObj.term.trim() !== '');
    setTermsWithColorCodes((termsWithColorCodes) => [
      ...termsWithColorCodes,
      ...updatedTermsList,
    ]);
    setIsDuplicateTermsHighlighted(duplicateTerms.length > 0);
    setTerms(duplicateTerms);
    setTimeout(() => {
      setCurrentSelectedErrorTerm(1);
      changeErrorTermValue(1);
    });
    setTermsValidationData([]);
  };

  const moveTermsToLeft = () => {
    const selectedTermColorMap = new Map();
    termsWithColorCodes.forEach((termItem: Terms) => {
      if (selectedRows.includes(termItem.term)) {
        selectedTermColorMap.set(termItem.term, termItem.backgroundColor);
      }
    });
    const selectedTerm = termsWithColorCodes
      .filter((termWithColorCode: Terms) =>
        selectedRows.includes(termWithColorCode.term)
      )
      ?.map((termWithColorCode) => termWithColorCode.term);
    const updatedTermsWithCodes = termsWithColorCodes.filter(
      (termWithColorCode: Terms) =>
        !selectedRows.includes(termWithColorCode.term)
    );
    setTermsWithColorCodes(
      updatedTermsWithCodes.map((termWithCode, index: number) => ({
        ...termWithCode,
        id: index + 1,
      }))
    );
    const mappedAdditionalTerms = selectedTerm.map((term) => ({
      value: term,
      isAPIErrorTerm: false,
    }));
    if (terms) {
      setTerms([...terms, ...mappedAdditionalTerms]);
    } else {
      setTerms([...mappedAdditionalTerms]);
    }
    setSelectedRows([]);
    setIsDuplicateTermsHighlighted(false);
    setTermsWithHighlightColorMap(selectedTermColorMap);
  };

  const setSelectedColor = (selectedColor: AurColorPickerOptionsProp) => {
    const updatedRows = termsWithColorCodes.map((row) =>
      selectedRows.includes(row.term) ? {...row, ...selectedColor} : row
    );
    setTermsWithColorCodes(updatedRows);
    setSelectedRows([]);
  };

  const onRowsSelectionChange = (newSelection: (string | number)[]) => {
    setSelectedRows(newSelection.length > 0 ? [...newSelection] : []);
  };

  const deleteTerms = () => {
    const updatedTermsWithCodes = termsWithColorCodes.filter(
      (termWithColorCode: Terms) =>
        !selectedRows.includes(termWithColorCode.term)
    );
    setTermsWithColorCodes(
      updatedTermsWithCodes.map((termWithCode, index: number) => ({
        ...termWithCode,
        id: index + 1,
      }))
    );
    setSelectedRows([]);
    setIsDuplicateTermsHighlighted(false);
    setShowDeleteConfirmationModal(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.DELETE,
      click: {
        name: t('gaTracking.global.ok'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.deleteTermOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.deleteTermsModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleUpdateHighlightedState = (updatedValue: boolean) => {
    setIsDuplicateTermsHighlighted(updatedValue);
  };

  useEffect(() => {
    onDataChange(termsWithColorCodes);
  }, [termsWithColorCodes]);

  useEffect(() => {
    if (isManualErrorSelection.current) {
      isManualErrorSelection.current = false;
      return;
    }

    const errorTerms = terms.filter((term) => term.isAPIErrorTerm);
    const highlightedIndex = errorTerms.findIndex(
      (term) => term.isAPIHighlightedTerm
    );

    if (highlightedIndex >= 0) {
      setCurrentSelectedErrorTerm(highlightedIndex + 1);
    } else if (errorTerms.length > 0) {
      const newIndex = Math.max(currentSelectedErrorTerm - 1, 1);
      setCurrentSelectedErrorTerm(newIndex);
      changeErrorTermValue(newIndex);
    }
  }, [terms]);

  useEffect(() => {
    const errors = terms.filter((term) => term.isAPIErrorTerm);
    if (errors.length === 0) {
      setAnnouncement(
        t('eca.searchTermReport.termsContent.ariaDescription.noError')
      );
    } else {
      setAnnouncement(
        t(
          'eca.searchTermReport.termsContent.ariaDescription.dynamicErrorDesc',
          {
            currentSelectedErrorTerm: currentSelectedErrorTerm,
            totalErrors: errors.length,
          }
        )
      );
    }
  }, [currentSelectedErrorTerm, terms]);

  const handleErrorTermClick = (errorIndexStr: number) => {
    isManualErrorSelection.current = true;
    setCurrentSelectedErrorTerm(errorIndexStr);
    changeErrorTermValue(errorIndexStr);
  };

  const moveToPreviousErrorTerm = () => {
    isManualErrorSelection.current = true;
    setCurrentSelectedErrorTerm(currentSelectedErrorTerm - 1);
    changeErrorTermValue(currentSelectedErrorTerm - 1);
  };

  const moveToNextErrorTerm = () => {
    isManualErrorSelection.current = true;
    setCurrentSelectedErrorTerm(currentSelectedErrorTerm + 1);
    changeErrorTermValue(currentSelectedErrorTerm + 1);
  };

  const changeErrorTermValue = (index: number) => {
    setTerms((prevTerms) => {
      let errorTermCount = 0;
      return prevTerms.map((term) => {
        if (term.isAPIErrorTerm) {
          errorTermCount++;
          if (errorTermCount === index) {
            return {
              ...term,
              isAPIHighlightedTerm: true,
            };
          } else {
            return {
              ...term,
              isAPIHighlightedTerm: false,
            };
          }
        }
        return {
          ...term,
          isAPIHighlightedTerm: false,
        };
      });
    });
  };

  return (
    <>
      <Box className="terms-content-main">
        <TermsContentHeader />
        {/* Tabs */}
        <AurCenteredTabs
          defaultValue={selectedTab}
          tabsArray={tabsArray}
          onTabSelection={handleTabSelection}
        />

        {/* Panels */}
        <Box className="terms-content-panels">
          {/* Left Panel */}
          {(selectedTab === STR_TERMS_TABS.FIELD_EDITOR ||
            selectedTab === STR_TERMS_TABS.SPLIT_VIEW) && (
            <Box className="terms-content-panels-left">
              <div className="terms-content-panels-left-heading-top">
                <div className="terms-content-panels-left-heading">
                  <Typography className="typo-text subhead3_size" tabIndex={0} role="button">
                    {t('eca.searchTermReport.termsContent.editor.heading')}
                  </Typography>
                  <Typography className="typo-text body3_size" tabIndex={0} role="button">
                    {t(
                      'eca.searchTermReport.termsContent.editor.subHeading'
                    )}{' '}
                  </Typography>
                </div>
                <AurPreviousNextNavigation
                  totalItems={
                    terms.filter((term) => term.isAPIErrorTerm).length
                  }
                  currentItem={currentSelectedErrorTerm}
                  onNextClick={moveToNextErrorTerm}
                  onPreviousClick={moveToPreviousErrorTerm}
                />
              </div>
              <Box sx={visuallyHidden} aria-live="polite">
                {announcement}
              </Box>
              <AurSlateEditor
                initialListData={terms}
                onChange={onChangeTerms}
                updateHighlightedState={handleUpdateHighlightedState}
                onErrorTermClick={handleErrorTermClick}
                focusedAPIErrorIndex={currentSelectedErrorTerm}
              />
              <div className="terms-content-panels-left-button d-flex align-items-center justify-content-end">
                <AurTooltip
                  title={t('eca.searchTermReport.termsContent.csvInfo')}
                  aria-label={t('eca.searchTermReport.termsContent.csvInfo')}
                  className="typo-text label3_size terms-tooltip-content"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    },
                  }}
                  tabIndex={-1}
                >
                  <InfoIcon className="terms-content-header-title-icon terms-info" />
                </AurTooltip>
                <AurButton
                  type="continue"
                  label={t('eca.searchTermReport.termsContent.importButton')}
                  ariaLabel={t(
                    'eca.searchTermReport.termsContent.importButton'
                  )}
                  size="Small"
                  startIcon={<DownloadIcon aria-hidden="true" />}
                  onClick={handleCsvUpload}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="csvUpload"
                  onChange={handleFileChange}
                />
              </div>
            </Box>
          )}
          {/* Middle Arrow Buttons */}
          {selectedTab === STR_TERMS_TABS.SPLIT_VIEW && (
            <TermsContentMiddle
              terms={terms}
              isTermHighlighted={isDuplicateTermsHighlighted}
              selectedRows={selectedRows}
              moveTermsToRight={handleMoveToRightClick}
              moveTermsToLeft={moveTermsToLeft}
            />
          )}

          {/* Right Panel */}
          {(selectedTab === STR_TERMS_TABS.SPLIT_VIEW ||
            selectedTab === STR_TERMS_TABS.TERMS_LIST) && (
            <TermsContentRight
              termsWithColorCodes={termsWithColorCodes}
              selectedRows={selectedRows}
              onRowsSelectionChange={onRowsSelectionChange}
              handleRowOrderChange={handleRowOrderChange}
              setSelectedColor={setSelectedColor}
              deleteTerms={() => setShowDeleteConfirmationModal(true)}
            />
          )}
        </Box>
        {showDeleteConfirmationModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={showDeleteConfirmationModal}
            onOk={deleteTerms}
            onCancel={() => {
              setShowDeleteConfirmationModal(false);
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.global.cancel'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.MODAL,
                  action: t('gaTracking.str.deleteTermCancelAction'),
                },
                subPage: {
                  subpage_name: t('gaTracking.str.deleteTermsModal'),
                  subpage_type: GA_SUBPAGE_TYPE.MODAL,
                },
              });
            }}
            popupHeading={t(
              'eca.searchTermReport.termsContent.deletePopup.heading'
            )}
            popupTitle={t(
              'eca.searchTermReport.termsContent.deletePopup.title'
            )}
            popupContent={t(
              'eca.searchTermReport.termsContent.deletePopup.subHeading'
            )}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            labelActionCancel={t('common.cancel')}
            labelActionOk={t('eca.searchTermReport.ok')}
          />
        )}
      </Box>
      {termsConfirmationModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={termsConfirmationModal}
          onOk={handleTermsModalOkClick}
          onCancel={handleTermsModalCancelClick}
          popupTitle={t(
            'eca.searchTermReport.termsContent.termsValidationPopup.title'
          )}
          popupHeading={t(
            'eca.searchTermReport.termsContent.termsValidationPopup.heading'
          )}
          popupContent={t(
            'eca.searchTermReport.termsContent.termsValidationPopup.subHeading'
          )}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t(
            'eca.searchTermReport.termsContent.termsValidationPopup.cancel'
          )}
          labelActionOk={t(
            'eca.searchTermReport.termsContent.termsValidationPopup.continue'
          )}
          exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
          continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
          cancelButtonTitle=""
        />
      )}

      {showQueryWarningModal && (
        <QueryWarningPopupAlert
          showDialog={showQueryWarningModal.visible}
          onOk={() => {
            setShowQueryWarningModal((prev) => ({...prev, visible: false}));
            moveTermsToRight(false, false, true);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.str.queryContinueButton'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.MODAL,
                action: t('gaTracking.str.queryIssueOkAction'),
              },
              subPage: {
                subpage_name: t('gaTracking.str.syntaxError'),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              },
            });
          }}
          queryErrorTypes={showQueryWarningModal.errorTypes ?? []}
          onCancel={() => {
            setShowQueryWarningModal((prev) => ({...prev, visible: false}));
            trackGAEvent({
              eventName: GA_EVENT_NAMES.ERROR,
              click: {
                name: t('gaTracking.global.cancel'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.MODAL,
                action: t('gaTracking.str.queryIssueCancelAction'),
              },
              subPage: {
                subpage_name: t('gaTracking.str.syntaxError'),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              },
            });
          }}
        />
      )}
    </>
  );
};

export default TermsContent;
